import { Injectable, inject } from "@angular/core";
import {
	Firestore,
	collection,
	doc,
	getDocs,
	getDoc,
	addDoc,
	updateDoc,
	query,
	where,
	orderBy,
} from "@angular/fire/firestore";
import { Observable, from, map } from "rxjs";
import { httpsCallable, Functions } from "@angular/fire/functions";
import { Auth } from "@angular/fire/auth";

import { Survey, RespondentSurvey } from "../models/survey.model";

@Injectable({
	providedIn: "root",
})
export class SurveyService {
	private firestore = inject(Firestore);
	private functions = inject(Functions);
	private auth = inject(Auth);

	// Helper method to get current user ID
	private getCurrentUserId(): string | null {
		const user = this.auth.currentUser;
		if (!user) {
			console.warn("No authenticated user found");
			return null;
		}
		return user.uid;
	}

	// Helper method to get current user ID with error handling
	private getCurrentUserIdOrThrow(): string {
		const userId = this.getCurrentUserId();
		if (!userId) {
			throw new Error("User not authenticated");
		}
		return userId;
	}

	// Get active surveys for the current respondent
	getActiveRespondentSurveys(): Observable<RespondentSurvey[]> {
		const userId = this.getCurrentUserIdOrThrow();

		// Query respondentSurveys collection for active surveys for this user
		const respondentSurveysQuery = query(
			collection(this.firestore, "respondentSurveys"),
			where("user_id", "==", userId),
			where("active", "==", true),
			orderBy("created_at", "desc"),
		);

		return from(getDocs(respondentSurveysQuery)).pipe(
			map((snapshot) => {
				const respondentSurveys = snapshot.docs.map((doc) => {
					const data = doc.data();
					return {
						id: doc.id,
						...data,
					};
				}) as RespondentSurvey[];

				// The respondentSurveys collection already contains all necessary display data
				return respondentSurveys;
			}),
		);
	}

	// Get a specific survey by ID
	getSurvey(id: string): Observable<Survey | null> {
		const surveyRef = doc(this.firestore, `surveys/${id}`);
		return from(getDoc(surveyRef)).pipe(
			map((doc) => {
				if (doc.exists()) {
					return { id: doc.id, ...doc.data() } as Survey;
				}
				return null;
			}),
		);
	}

	// Create a new survey
	createSurvey(data: Partial<Survey>): Observable<string> {
		const surveysRef = collection(this.firestore, "surveys");
		return from(addDoc(surveysRef, data)).pipe(map((docRef) => docRef.id));
	}

	// Update a survey
	updateSurvey(id: string, data: Partial<Survey>): Observable<void> {
		const surveyRef = doc(this.firestore, `surveys/${id}`);
		return from(updateDoc(surveyRef, data));
	}

	// Get respondent survey details
	getRespondentSurvey(organizationSurveyId: string, memberId: string): Observable<any> {
		const respondentRef = doc(
			this.firestore,
			`organizationSurveys/${organizationSurveyId}/respondents/${memberId}`,
		);

		return from(getDoc(respondentRef)).pipe(
			map((doc) => {
				if (doc.exists()) {
					return { id: doc.id, ...doc.data() };
				}
				return null;
			}),
		);
	}

	// Submit an answer for a survey question
	submitAnswer(
		organizationSurveyId: string,
		memberId: string,
		question_id: string,
		answer: any,
	): Observable<any> {
		const submitAnswerFunction = httpsCallable(this.functions, "submitAnswerToSurvey");
		return from(
			submitAnswerFunction({
				organization_survey_id: organizationSurveyId,
				user_id: memberId,
				question_id,
				answer,
			}),
		);
	}

	// Submit a completed survey (flip status to completed, set completed_at)
	submitCompletedSurvey(organizationSurveyId: string, userId: string): Observable<any> {
		const submitFn = httpsCallable(this.functions, "submitCompletedSurvey");
		return from(
			submitFn({
				organization_survey_id: organizationSurveyId,
				user_id: userId,
			}),
		);
	}

	// Record the respondent clicking "Begin" on the first section intro
	// Idempotent — if started_at is already set, the backend no-ops.
	startSurvey(organizationSurveyId: string, userId: string): Observable<any> {
		const startFn = httpsCallable(this.functions, "startSurvey");
		return from(
			startFn({
				organization_survey_id: organizationSurveyId,
				user_id: userId,
			}),
		);
	}
}
